// {{WORKTREE_ROOT}}/picosponsorconsole/app/root.tsx
// PLAN2 WP10 — the root loader NEVER throws except the host redirect (/ on the Room and global hosts): a path that does
// not belong on this host renders the NotFound page with a real 404 status and the locale still available (D10-4).
// Per request: a nonce, the CSP (with the RL-27 relaxations), the active locale slice only (§5.10), a CSRF token bound
// to the session, and the <html> attributes (theme, text mode, accent, surface, region) so the first paint is right.
// The app is wrapped once in DesignI18nProvider (R-35: design components contain no English and throw without it).
import { json, redirect, type HeadersFunction, type LinksFunction, type LoaderFunctionArgs, type MetaFunction } from "@remix-run/node";
import { Links, Meta, Outlet, Scripts, ScrollRestoration, useLoaderData, useRouteError, useRouteLoaderData } from "@remix-run/react";
import tokens from "@picosponsor/design/tokens.css?url";
import fonts from "@picosponsor/design/fonts.css?url";
import { DesignI18nProvider, DesignLinkProvider, type LinkComponent } from "@picosponsor/design";
import { useMemo, type ComponentProps, type ReactNode } from "react";
import appCss from "./css/app.css?url";
import { env } from "~/utils/env.server";
import { hostDecision, requestHost } from "~/utils/host.server";
import { buildCsp, buildPermissionsPolicy, newNonce, securityHeaders } from "~/utils/csp.server";
import { getSessionQuiet } from "~/utils/session.server";
import { bundleFor, negotiateLang } from "~/utils/locale.server";
import { csrfToken } from "~/utils/csrf.server";
import { readPrefs } from "~/utils/prefs";
import { LocaleProvider, useLocale, useT } from "~/utils/locale";
import { describeThrown } from "~/utils/loadState";
import { PRE_PAINT_SCRIPT } from "~/utils/prePaint";
import { BOUNDARY_STRINGS } from "~/utils/locale/boundary.generated";
import { RemixLink } from "~/components/shell/RemixLink";
import { LiveRegionProvider } from "~/components/shell/LiveRegion";
import { SkipLink } from "~/components/shell/SkipLink";
import { MachineTranslationNoteSlot } from "~/components/shell/machineTranslationSlot";
import { AnalyticsPageViews } from "~/components/AnalyticsPageViews";
import { NotFound } from "~/components/states/NotFound";
import { TESTIDS } from "~/utils/testids";
import { THEME_COLOR } from "~/config/themeColor";

export const links: LinksFunction = () => [
  // ── PLAN11 D11-26 ── the Mindario v3 mark: M + chevrons on a black tile (design handoff brand/)
  { rel: "icon", href: "/favicon.svg", type: "image/svg+xml" },
  { rel: "icon", href: "/favicon.ico", sizes: "48x48" },
  { rel: "apple-touch-icon", href: "/apple-touch-icon.png" },
  { rel: "stylesheet", href: fonts },
  { rel: "stylesheet", href: tokens },
  { rel: "stylesheet", href: appCss },
];

export async function loader({ request }: LoaderFunctionArgs) {
  const decision = hostDecision(request);
  if (decision.kind === "redirect") throw redirect(decision.to);
  const e = env();
  const host = decision.host ?? "app";
  const url = new URL(request.url);
  // PLAN-BEST-EFFORT: the root only needs lang/theme from the session; a session-store failure surfaces in _auth's loader.
  const session = host === "global" ? null : await getSessionQuiet(request);
  const prefs = readPrefs(request.headers.get("cookie"));
  const lang = negotiateLang({ sessionLang: session?.lang ?? null, cookieLang: prefs.lang, acceptLanguage: request.headers.get("accept-language"), stage: e.stage });
  const bundle = bundleFor(lang);
  // ── WP33 ── D33-12: off-prod only, ps_dir=rtl renders the page right-to-left for the RTL smoke (Cypress 333). Prod ignores it.
  const dirOverride = e.stage !== "prod" && /(?:^|;\s*)ps_dir=rtl(?:;|$)/.test(request.headers.get("cookie") ?? "") ? ("rtl" as const) : null;
  const nonce = newNonce();
  const local = e.stage === "dev" && url.protocol === "http:" && /\.localhost(:\d+)?$/.test(requestHost(request)); // R-249: npm run dev:local
  const csp = buildCsp({ nonce, pathname: url.pathname, host, env: e, dev: process.env.NODE_ENV !== "production", local });
  return json(
    {
      lang: bundle.lang, dir: dirOverride ?? bundle.dir, locale: bundle, terms: prefs.terms, reviewed: bundle.reviewed,
      theme: session?.theme ?? prefs.theme, textMode: session?.textMode ?? prefs.textMode,
      accent: e.accent, region: host === "global" ? "global" : e.regionCode,
      // ── WP20 ── the capture PWA is its own surface (D20-17); night mode is the person's choice (D20-21).
      surface: host === "room" ? "room" : host === "app" && (url.pathname === "/capture" || url.pathname.startsWith("/capture/")) ? "capture" : "console",
      captureMode: prefs.captureMode,
      hostKind: host, stage: e.stage, nonce, csrf: await csrfToken(request), notFound: decision.kind === "not-found",
    },
    { status: decision.kind === "not-found" ? 404 : 200, headers: { "x-ps-csp": csp, "x-ps-pp": buildPermissionsPolicy(url.pathname, host) } },
  );
}

export const headers: HeadersFunction = ({ loaderHeaders }) => securityHeaders(loaderHeaders.get("x-ps-csp"), loaderHeaders.get("x-ps-pp"));

export const meta: MetaFunction<typeof loader> = ({ data }) => [
  { title: data?.locale.strings.common?.["product-name"] ?? "" },
  { name: "robots", content: "noindex, nofollow" },
];

export function Layout({ children }: { children: React.ReactNode }) {
  const data = useRouteLoaderData<typeof loader>("root");
  return (
    <html
      lang={data?.lang ?? "en-US"} dir={data?.dir ?? "ltr"}
      data-theme={data?.theme ?? "dark"} data-text={data?.textMode ?? "standard"} data-accent={data?.accent ?? "green"}
      data-surface={data?.surface ?? "console"} data-region={data?.region ?? ""}
      data-capture-mode={data?.surface === "capture" && data?.captureMode === "night" ? "night" : undefined}
      suppressHydrationWarning
    >
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="color-scheme" content="dark light" />
        <meta name="theme-color" content={THEME_COLOR} />
        <Meta />
        <Links />
        {/* The one dangerouslySetInnerHTML in the app: a constant, nonce-bound string with no user data (react/no-danger
            is allow-listed for this file only). Omitted when the root loader produced no nonce. */}
        {data?.nonce ? <script nonce={data.nonce} dangerouslySetInnerHTML={{ __html: PRE_PAINT_SCRIPT }} /> : null}
      </head>
      <body className="bg-bg text-ink">
        {children}
        <AnalyticsPageViews />
        <ScrollRestoration nonce={data?.nonce} />
        <Scripts nonce={data?.nonce} />
      </body>
    </html>
  );
}

/** R-35: the design package reads its ids through this provider; `t` is the console's (the `-terms` layer included). */
function DesignI18nBridge({ children }: { children: ReactNode }) {
  const t = useT();
  const { lang, dir } = useLocale();
  const value = useMemo<ComponentProps<typeof DesignI18nProvider>["value"]>(() => ({
    t: (msg, args) => { const [section, id] = msg as readonly [string, string]; return t(section, id, args); },
    lang, dir,
  }), [t, lang, dir]);
  return <DesignI18nProvider value={value}>{children}</DesignI18nProvider>;
}

export default function App() {
  const data = useLoaderData<typeof loader>();
  return (
    <LocaleProvider bundle={data.locale} terms={data.terms}>
      <DesignI18nBridge>
        <LiveRegionProvider>
          <DesignLinkProvider component={RemixLink as LinkComponent}>
            <SkipLink />
            {!data.reviewed && MachineTranslationNoteSlot ? <MachineTranslationNoteSlot hostKind={data.hostKind} /> : null}
            <div className="cq min-h-screen">{data.notFound ? <NotFound /> : <Outlet />}</div>
          </DesignLinkProvider>
        </LiveRegionProvider>
      </DesignI18nBridge>
    </LocaleProvider>
  );
}

/** C-102: localised from the root loader's slice (it renders outside App's providers), three shapes: 404 / signed out /
 * other. Links are full navigations on purpose (the app may be broken). Off-prod, the mono line carries code + request. */
export function ErrorBoundary() {
  const error = useRouteError();
  const data = useRouteLoaderData<typeof loader>("root");
  const d = describeThrown(error);
  if (!data) {
    return (
      <main id="main" className="mx-auto flex w-full max-w-[560px] flex-col gap-4 px-5 pb-6 pt-12" data-testid={TESTIDS.states.rootError}>
        <h1 className="display m-0 text-28">{d.status === 404 ? BOUNDARY_STRINGS.notFoundTitle : BOUNDARY_STRINGS.title}</h1>
        <p className="m-0 text-15 text-dim">{BOUNDARY_STRINGS.body}</p>
        {/* PLAN-ANCHOR-OK: root boundary without loader data — a full reload is the only way forward */}
        <a href="/" className="text-15 font-bold text-accent underline">{BOUNDARY_STRINGS.reload}</a>
      </main>
    );
  }
  return (
    <LocaleProvider bundle={data.locale} terms={data.terms}>
      <DesignI18nBridge>
        <RootErrorBody status={d.status} code={d.code} requestId={d.requestId} showDetail={data.stage !== "prod"} />
      </DesignI18nBridge>
    </LocaleProvider>
  );
}

function RootErrorBody({ status, code, requestId, showDetail }: { status: number; code: string; requestId: string | null; showDetail: boolean }) {
  const t = useT();
  if (status === 404) return <NotFound />;
  const signedOut = status === 401;
  return (
    <main id="main" className="mx-auto flex w-full max-w-[560px] flex-col gap-4 px-5 pb-6 pt-12" data-testid={TESTIDS.states.rootError}>
      <h1 className="display m-0 text-28 leading-0.95">{t("states", signedOut ? "signed-out-title" : "load-failed-title")}</h1>
      <p className="m-0 text-15 leading-1.5 text-dim">{t("states", signedOut ? "signed-out-body" : "load-failed-body")}</p>
      {showDetail && !signedOut ? <p className="mono m-0 text-11 text-faint">{requestId ? t("states", "load-failed-detail", { requestId, code }) : t("states", "load-failed-code", { code })}</p> : null}
      <div className="flex flex-wrap gap-2.5">
        {/* PLAN-ANCHOR-OK: root boundary — a full navigation is the point (the client app may be the thing that failed) */}
        <a href={signedOut ? "/signin" : "/"} className="inline-flex h-11 items-center rounded-round bg-cta px-5 text-14 font-bold text-on-cta no-underline hover:bg-cta-hover focus-visible:bg-cta-hover">{t("states", signedOut ? "sign-in-again" : "back-home")}</a>
      </div>
    </main>
  );
}
